<script setup lang="ts">
import { isBackgroundNetworkError } from '@/composables/backgroundRequests';
import { showErrorToast } from '@/composables/useMutation';
import { HttpNetworkError } from '@inertiajs/core';
import { router } from '@inertiajs/vue3';
import { trans } from 'laravel-vue-i18n';
import { Check, Info, TriangleAlert, X, LoaderCircle } from 'lucide-vue-next';
import { computed, onMounted, onUnmounted } from 'vue';
import { useToastStore, type ToastNotification } from '@/stores/toastStore';
import { Link } from '@inertiajs/vue3';

const toasts = useToastStore();
const toastNotifications = computed(() => toasts.notifications);
// Closed with its button: the opt-in `onClose` may give the focus back (the button leaves the page).
const closeNotification = (notification: ToastNotification) => {
    toasts.dismiss(notification.id);
    notification.onClose?.();
};
// Opt-in `action.onVisit`: the visit of the action has started, the toast has done its job.
const visitAction = (notification: ToastNotification) => {
    toasts.dismiss(notification.id);
    notification.action?.onVisit?.();
};
// Opt-in `focusAction`: the action link takes the focus once, when the toast shows.
const focusedActions = new Set<string>();
const focusAction = (notification: ToastNotification, element: unknown) => {
    const key = notification.lifetimeKey ?? notification.id;
    if (!notification.focusAction || !element || focusedActions.has(key))
        return;
    focusedActions.add(key);
    const target = ((element as { $el?: unknown }).$el ?? element) as {
        focus?: () => void;
    };
    target.focus?.();
};
const getIcon = (type: ToastNotification['type']) =>
    type === 'success'
        ? Check
        : type === 'warning'
          ? TriangleAlert
          : type === 'info'
            ? Info
            : X;
const getIconColor = (type: ToastNotification['type']) =>
    type === 'success'
        ? 'text-positive'
        : type === 'warning'
          ? 'text-alert-ink'
          : type === 'info'
            ? 'text-info'
            : 'text-negative';
const showToast = (toast: Omit<ToastNotification, 'id'> & { id?: string }) =>
    toasts.upsert(toast);

const handleFlash = (event: any): void => {
    if (event.detail.flash?.toast) {
        showToast(event.detail.flash.toast);
    }
};

/**
 * Erreur réseau d'une visite Inertia (useForm, router.*) que l'appelant n'a
 * pas traitée : toast générique. useMutation traite déjà la sienne
 * (`onNetworkError` renvoie false) et l'événement global n'est alors pas émis.
 */
const handleNetworkError = (event: any): void => {
    if (!(event.detail?.error instanceof HttpNetworkError)) return;
    if (!isBackgroundNetworkError(event.detail.error)) {
        showErrorToast(trans('common.errors.network'));
    }
    // Erreur signalée : pas de rejet de promesse non capturé.
    event.preventDefault();
};

// Seul le flash Inertia (`Inertia::flash`, `router.flash`) affiche un toast :
// il n'est pas conservé dans l'historique, contrairement aux props de la page,
// et ne réapparaît donc pas au retour arrière.
onMounted(() => {
    const stopFlash = router.on('flash', handleFlash);
    const stopNetwork = router.on('networkError', handleNetworkError);
    onUnmounted(() => {
        stopFlash();
        stopNetwork();
        toasts.cleanup();
    });
});
</script>

<template>
    <!-- Mobile : au-dessus de la navigation basse d'AppLayout
         (--mobile-navigation-height, définie à la racine dans app.css tant
         que cette navigation existe ; 0 ailleurs). -->
    <div
        class="pointer-events-none fixed inset-x-0 top-16 bottom-[var(--mobile-navigation-height,0px)] z-[150] flex items-end px-4 py-6 sm:items-start sm:p-6 md:bottom-0"
    >
        <div
            class="flex max-h-full w-full flex-col items-center space-y-3 overflow-y-auto sm:items-end"
        >
            <transition-group
                :key="toasts.renderKey"
                enter-active-class="transform ease-out duration-300 transition"
                enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
                enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
                leave-active-class="transition ease-in duration-150"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <div
                    v-for="notification in toastNotifications"
                    :key="notification.id"
                    :role="notification.type === 'error' ? 'alert' : 'status'"
                    :aria-live="
                        notification.type === 'error' ? 'assertive' : 'polite'
                    "
                    class="border-border bg-surface pointer-events-auto w-full max-w-sm overflow-hidden rounded-xl border shadow-[0_8px_20px_-12px_rgba(60,40,20,0.2)]"
                >
                    <div class="p-4">
                        <div class="flex items-start">
                            <div class="shrink-0 pt-0.5">
                                <component
                                    :is="
                                        notification.loading
                                            ? LoaderCircle
                                            : getIcon(notification.type)
                                    "
                                    :class="[
                                        'h-5 w-5',
                                        notification.loading && 'animate-spin',
                                        getIconColor(notification.type),
                                    ]"
                                />
                            </div>
                            <div class="ml-3 w-0 flex-1">
                                <template v-if="notification.title">
                                    <p class="text-ink text-sm font-semibold">
                                        {{ notification.title }}
                                    </p>
                                    <p class="text-text-soft mt-0.5 text-sm">
                                        {{ notification.message }}
                                    </p>
                                </template>
                                <template v-else>
                                    <p class="text-ink text-sm">
                                        {{ notification.message }}
                                    </p>
                                </template>
                                <Link
                                    v-if="notification.action"
                                    :ref="
                                        (element) =>
                                            focusAction(notification, element)
                                    "
                                    :href="notification.action.href"
                                    @start="
                                        notification.action.onVisit &&
                                        visitAction(notification)
                                    "
                                    class="text-info mt-2 inline-block text-sm font-medium underline"
                                    >{{ notification.action.label }}</Link
                                >
                            </div>
                            <div class="ml-4 flex shrink-0">
                                <button
                                    type="button"
                                    @click="closeNotification(notification)"
                                    class="text-muted hover:text-text-soft focus:ring-terracotta-soft inline-flex rounded-md focus:ring-2 focus:outline-none"
                                >
                                    <span class="sr-only">{{
                                        $t('common.close')
                                    }}</span>
                                    <X class="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div
                        v-if="
                            notification.timeout !== null &&
                            !notification.loading
                        "
                        class="bg-surface-2 h-0.5 w-full"
                        aria-hidden="true"
                    >
                        <div
                            :key="notification.lifetimeKey"
                            class="toast-countdown bg-info h-full"
                            :style="{
                                animationDuration: `${notification.timeout ?? 5000}ms`,
                            }"
                        />
                    </div>
                </div>
            </transition-group>
        </div>
    </div>
</template>

<style scoped>
.toast-countdown {
    transform-origin: left;
    animation-name: toast-lifetime;
    animation-timing-function: linear;
    animation-fill-mode: forwards;
}
@keyframes toast-lifetime {
    from {
        transform: scaleX(1);
    }
    to {
        transform: scaleX(0);
    }
}
</style>
